import { Timeline as AntdTimeline, TimelineItemProps } from "antd/es";
import { Guid } from "guid-typescript";
import { CSSProperties, PureComponent, ReactNode } from "react";
import isEqual from "react-fast-compare";
import withCommonEvents from "../../../shared/hoc/with-common-events";
import DynamicStyleManager from "../../../shared/utilty/dynamic-style-manager";
import "./timeline.scss";

declare let window: Window & { [key: string]: any };

export interface ITimelineProps {
  className?: string;
  style?: CSSProperties;
  options?: object[];
  datatitlefield?: string;
  datadescriptionfield?: string;
  datacolorfield?: string;
  mode: "left" | "alternate" | "right";
}

interface ITimelineState {}

class Timeline extends PureComponent<ITimelineProps, ITimelineState> {
  private uniqueKey = Guid.create();

  private style = new DynamicStyleManager(`ktimeline_${this.uniqueKey}`);

  private readonly isDashboard: boolean;

  private static readonly dashboardDataTitleField?: string = "label";

  private static readonly dashboardDataDescriptionField?: string = "children";

  private static readonly dashboardDataColorField?: string = "colr";

  private dataTitleField?: string;

  private dataDescriptionField?: string;

  private dataColorField?: string;

  private static readonly dashboardData = [
    {
      label: "1.",
      color: "green",
      children: "Create a services site 2015-09-01"
    },
    {
      label: "2.",
      color: "red",
      children: "Solve initial network problems 2015-09-01"
    }
  ] as TimelineItemProps[];

  constructor(props: ITimelineProps) {
    super(props);
    this.state = { uniqueKey: Guid.create() };

    this.isDashboard = window.kuika?.dashboardState === 1;
    this.initializeFieldKeys();
  }

  private initializeFieldKeys = (): void => {
    if (this.isDashboard) {
      this.dataTitleField = Timeline.dashboardDataTitleField;
      this.dataDescriptionField = Timeline.dashboardDataDescriptionField;
      this.dataColorField = Timeline.dashboardDataColorField;
    } else {
      this.dataTitleField = this.props.datatitlefield;
      this.dataDescriptionField = this.props.datadescriptionfield;
      this.dataColorField = this.props.datacolorfield;
    }
  };

  componentDidMount() {
    this.setDynamicStyle();
  }

  componentDidUpdate(prevProps: ITimelineProps) {
    if (!isEqual(prevProps, this.props)) {
      this.setDynamicStyle();
    }
  }

  getDynamicCss = (): string => {
    const className: string = this.getClassName();
    if (!className || className.length === 0) {
      return "";
    }
    let result = "";
    const { paddingTop, fontSize, borderStyle, borderBottomWidth, borderLeftWidth, borderRightWidth, borderTopWidth } =
      this.props.style;
    if (!paddingTop) {
      result += `.${className.trim()} {
          padding-top: 8px !important;
        }`;
    }
    const antTimeLineItemLabel = document.querySelector(`.${className.trim()} .ant-timeline-item-label`);
    if (antTimeLineItemLabel) {
      const width = antTimeLineItemLabel.clientWidth;
      result += `.${className.trim()} .ant-timeline-item-content {
          width: ${width - 16}px !important;
        }`;
    }
    if (fontSize) {
      result += `.${className.trim()} .ant-timeline-item-label {
          font-size: ${fontSize} !important;
        }`;

      result += `.${className.trim()} .ant-timeline-item-content {
          font-size: ${fontSize} !important;
        }`;
    }
    if (
      !borderStyle &&
      (borderBottomWidth || borderTopWidth || borderBottomWidth || borderLeftWidth || borderRightWidth)
    ) {
      result += `.${className.trim()} {
          border-style: solid;
        }`;
    }
    return result;
  };

  setDynamicStyle = () => {
    this.style.setDynamicCss(this.getDynamicCss());
  };

  private getClassName = (): string => {
    let result = "";

    if (this.props.className) {
      result = `${this.props.className} `;
    }

    if (!this.uniqueKey) {
      return result;
    }

    result = `${result}Timeline_${this.uniqueKey.toString()}`;
    return result;
  };

  private getItems = (): TimelineItemProps[] => {
    // Dashboard mode (designer canvas / preview) used to ignore the
    // caller-supplied `options` prop and always render the hardcoded
    // 2-event placeholder. That prevented the Designer agent from
    // previewing a populated Timeline in decorative mockups (e.g. a
    // "timeline: 5 olay" request produced a Timeline that visually
    // only showed 2 platform-default rows, and the agent would route
    // around it by hand-drawing custom timeline-item panels). Prefer
    // the caller's options whenever they were supplied; fall back to
    // the hardcoded sample only when nothing was passed, so a bare
    // `<Timeline/>` on canvas still shows a placeholder.
    const callerOptions = this.props.options;
    const useCallerOptions = Array.isArray(callerOptions) && callerOptions.length > 0;
    const source = this.isDashboard && !useCallerOptions ? Timeline.dashboardData : (callerOptions ?? []);
    // Field mapping: in dashboard mode without caller options, the
    // hardcoded sample shape ({label, color, children}) is the source
    // of truth. When the caller hands us options, honor their
    // datatitlefield / datadescriptionfield / datacolorfield mapping
    // if present; default to the dashboard sample shape so a quick
    // inline `options=[{label, color, children}]` works without
    // configuring the field props.
    let titleField = this.dataTitleField;
    let descriptionField = this.dataDescriptionField;
    let colorField = this.dataColorField;
    if (this.isDashboard && useCallerOptions) {
      titleField = this.props.datatitlefield ?? Timeline.dashboardDataTitleField;
      descriptionField = this.props.datadescriptionfield ?? Timeline.dashboardDataDescriptionField;
      colorField = this.props.datacolorfield ?? Timeline.dashboardDataColorField;
    }
    const items = source.map((item) => {
      return {
        label: item[titleField],
        children: item[descriptionField],
        color: item[colorField]
      };
    });
    return items;
  };

  private getStyle = (): CSSProperties => {
    const style: CSSProperties = { ...this.props.style };
    return style;
  };

  render(): ReactNode {
    return (
      <AntdTimeline
        mode={this.props.mode}
        items={this.getItems()}
        className={this.getClassName()}
        style={this.getStyle()}
      />
    );
  }
}

const timeline = withCommonEvents(Timeline);
export { timeline as Timeline };
